import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useIntl } from 'ha/i18n';
import { ListingKind } from '@ha/api/v2/types/Listing';
import { ListingTypeTranslations } from 'ha/models/Listing/translations';
import { countries } from 'ha/constants/Countries';
import {
  getCity,
  getCityLocalized,
  getCountry,
  getCountryCode,
  getListingIsCharitable,
  getCountryLocalized,
  getSearchURL,
  getStreet,
  getType,
  getKind,
  getHasError,
} from '../../selectors';
import { getCanonicalCategory } from '../../helpers/getCanonicalCategory';

export function useListingBreadcrumbs() {
  const location = useLocation();

  const { T, urlResolver, lang } = useIntl();

  const city = useSelector(getCity);
  const cityLocalized = useSelector(getCityLocalized);
  const country = useSelector(getCountry);
  const countryLocalized = useSelector(getCountryLocalized);
  const countryCode = useSelector(getCountryCode);
  const isCharitable = useSelector(getListingIsCharitable);
  const searchUrl = useSelector(getSearchURL);
  const street = useSelector(getStreet);
  const type = useSelector(getType);
  const kind = useSelector(getKind);
  const hasError = useSelector(getHasError);

  const listingType =
    kind === ListingKind.EntirePlace
      ? T(ListingTypeTranslations[type])
      : T('Room');

  const canonicalCategory = getCanonicalCategory({ kind, type }, lang);

  const breadcrumbs = React.useMemo(
    () =>
      isCharitable
        ? [
            {
              name: T('Search'),
              path: urlResolver.absolutizePath(
                urlResolver.getSearchUkraineUrl(),
                { skipAddLanguage: true },
              ),
            },
            {
              name: countryLocalized,
              path: urlResolver.absolutizePath(
                urlResolver.getSearchUkraineUrl(
                  countries.find(
                    countryObj => countryObj.code === countryCode.toUpperCase(),
                  )?.name ?? country,
                ),
                { skipAddLanguage: true },
              ),
            },
            {
              name: street,
              schemaName: T('%(type)s at %(street)s', {
                type: listingType,
                street,
              }),
              path: urlResolver.absolutizePath(location.pathname, {
                skipAddLanguage: true,
              }),
            },
          ]
        : [
            {
              name: cityLocalized || city,
              path: urlResolver.absolutizePath(
                urlResolver.getSearchUrl(
                  cityLocalized || city,
                  countryLocalized || country,
                ),
                { skipAddLanguage: true },
              ),
            },
            {
              name: searchUrl.name,
              path: urlResolver.absolutizePath(
                canonicalCategory
                  ? urlResolver.getSearchUrl(
                      cityLocalized || city,
                      countryLocalized || country,
                      {},
                      canonicalCategory,
                    )
                  : searchUrl.path,
                {
                  skipAddLanguage: true,
                },
              ),
            },
            {
              name: street,
              schemaName: T('%(type)s at %(street)s', {
                type: listingType,
                street,
              }),
              path: urlResolver.absolutizePath(location.pathname, {
                skipAddLanguage: true,
              }),
            },
          ],
    [
      T,
      canonicalCategory,
      city,
      cityLocalized,
      country,
      countryCode,
      countryLocalized,
      listingType,
      location.pathname,
      searchUrl.name,
      searchUrl.path,
      street,
      isCharitable,
      urlResolver,
    ],
  );

  return { hasError, breadcrumbs };
}
